<script lang="ts">
  import { usersApi, tripsApi, type UserSummary } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Modal from "../overlay/Modal.svelte";

  let {
    show = false,
    tripId,
    companions = [],
    onClose,
    onRefresh,
  }: {
    show?: boolean;
    tripId: string;
    companions?: Array<{
      id: string;
      user: UserSummary;
      role: string;
    }>;
    onClose?: () => void;
    onRefresh?: () => void;
  } = $props();

  let searchQuery = $state("");
  let searchResults: UserSummary[] = $state([]);
  let searching = $state(false);
  let inviting = $state(false);
  let searchTimeout: ReturnType<typeof setTimeout>;

  const roles = [
    { key: "owner", label: "소유자", description: "모든 권한", color: "bg-purple-100 text-purple-700" },
    { key: "editor", label: "편집자", description: "수정 가능", color: "bg-blue-100 text-blue-700" },
    { key: "member", label: "멤버", description: "지출 추가", color: "bg-green-100 text-green-700" },
    { key: "viewer", label: "뷰어", description: "보기만", color: "bg-gray-100 text-gray-700" },
  ];

  function handleSearch() {
    clearTimeout(searchTimeout);
    if (searchQuery.trim().length < 2) {
      searchResults = [];
      return;
    }

    searchTimeout = setTimeout(async () => {
      searching = true;
      try {
        const result = await usersApi.search(searchQuery);
        if (result.success) {
          const companionIds = companions.map(c => c.user.id);
          searchResults = (result.data || []).filter(u => !companionIds.includes(u.id));
        }
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        searching = false;
      }
    }, 300);
  }

  async function inviteUser(userId: string, role: string = "member") {
    inviting = true;
    try {
      const result = await tripsApi.addCompanion(tripId, userId, role);
      if (result.success) {
        addNotification("success", "동행자가 추가되었습니다!");
        searchQuery = "";
        searchResults = [];
        onRefresh?.();
      }
    } catch (error: any) {
      addNotification("error", error.response?.data?.errors?.[0]?.message || "동행자 추가에 실패했습니다.");
    } finally {
      inviting = false;
    }
  }

  async function removeCompanion(companionId: string) {
    if (!confirm("이 동행자를 제거하시겠습니까?")) return;

    try {
      await tripsApi.removeCompanion(tripId, companionId);
      addNotification("success", "동행자가 제거되었습니다.");
      onRefresh?.();
    } catch (error: any) {
      addNotification("error", "동행자 제거에 실패했습니다.");
    }
  }

  function getRoleInfo(roleKey: string) {
    return roles.find(r => r.key === roleKey) || roles[2];
  }

  function close() {
    searchQuery = "";
    searchResults = [];
    onClose?.();
  }
</script>

<Modal {show} title="동행자 관리" size="lg" onClose={close}>
  <div class="space-y-6">
    <!-- Search Section -->
    <div>
      <label for="search" class="block text-sm font-medium text-gray-700 mb-2">
        사용자 검색하여 초대하기
      </label>
      <div class="relative">
        <input
          id="search"
          type="text"
          bind:value={searchQuery}
          oninput={handleSearch}
          class="w-full px-4 py-3 pl-10 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
          placeholder="이름 또는 닉네임으로 검색..."
        />
        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {#if searching}
          <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        {/if}
      </div>

      {#if searchResults.length > 0}
        <div class="mt-2 bg-gray-50 rounded-xl p-2 max-h-48 overflow-y-auto">
          {#each searchResults as user}
            <div class="flex items-center justify-between p-2 hover:bg-white rounded-lg transition-colors">
              <div class="flex items-center gap-3">
                {#if user.avatar_url}
                  <img src={user.avatar_url} alt={user.name} class="w-10 h-10 rounded-full object-cover" />
                {:else}
                  <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white font-medium">
                    {user.name.charAt(0)}
                  </div>
                {/if}
                <div>
                  <div class="font-medium text-gray-900">{user.name}</div>
                  {#if user.nickname}
                    <div class="text-sm text-gray-500">@{user.nickname}</div>
                  {/if}
                </div>
              </div>
              <button
                onclick={() => inviteUser(user.id)}
                disabled={inviting}
                class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {inviting ? "초대 중..." : "초대"}
              </button>
            </div>
          {/each}
        </div>
      {:else if searchQuery.length >= 2 && !searching}
        <p class="text-sm text-gray-500 mt-2 text-center py-4">검색 결과가 없습니다</p>
      {/if}
    </div>

    <!-- Current Companions -->
    <div>
      <h3 class="text-sm font-medium text-gray-700 mb-3">
        현재 동행자 ({companions.length}명)
      </h3>
      
      {#if companions.length === 0}
        <div class="text-center py-8 text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p>아직 동행자가 없습니다</p>
          <p class="text-sm mt-1">위에서 검색하여 친구를 초대하세요!</p>
        </div>
      {:else}
        <div class="space-y-2">
          {#each companions as companion}
            {@const roleInfo = getRoleInfo(companion.role)}
            <div class="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div class="flex items-center gap-3">
                {#if companion.user.avatar_url}
                  <img src={companion.user.avatar_url} alt={companion.user.name} class="w-10 h-10 rounded-full object-cover" />
                {:else}
                  <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white font-medium">
                    {companion.user.name.charAt(0)}
                  </div>
                {/if}
                <div>
                  <div class="font-medium text-gray-900">{companion.user.name}</div>
                  <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium {roleInfo.color}">
                    {roleInfo.label}
                  </span>
                </div>
              </div>
              
              {#if companion.role !== "owner"}
                <button
                  onclick={() => removeCompanion(companion.id)}
                  class="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="동행자 제거"
                  aria-label="동행자 제거"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              {:else}
                <span class="text-xs text-gray-400">소유자</span>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </div>

    <!-- Invite Link -->
    <div class="pt-4 border-t border-gray-100">
      <h3 class="text-sm font-medium text-gray-700 mb-2">초대 링크로 공유하기</h3>
      <div class="flex gap-2">
        <input
          type="text"
          readonly
          value={`${typeof window !== 'undefined' ? window.location.origin : ''}/trips/${tripId}/join`}
          class="flex-1 px-4 py-2 bg-gray-100 rounded-lg text-sm text-gray-600"
        />
        <button
          onclick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/trips/${tripId}/join`);
            addNotification("success", "링크가 복사되었습니다!");
          }}
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
        >
          복사
        </button>
      </div>
    </div>
  </div>
</Modal>
